/**
 * Property Viewings API
 * RTK Query endpoints for property viewing management
 */

import { baseApi } from "./baseApi";

/**
 * Property Viewing interface
 */
export interface PropertyViewing {
	id: string;
	organization?: string;
	organization_name?: string;
	property: string;
	property_name?: string;
	property_address?: string;
	buyer_name: string;
	buyer_email?: string;
	buyer_phone?: string;
	viewing_date: string; // ISO datetime string
	duration: number; // minutes
	status: "pending" | "scheduled" | "confirmed" | "completed" | "cancelled";
	notes?: string;
	agent?: string;
	agent_name?: string;
	created_by?: string;
	created_by_name?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Property Viewing list response interface
 */
export interface PropertyViewingListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: PropertyViewing[];
}

/**
 * Create/Update property viewing request interface
 */
export interface PropertyViewingData {
	organization?: string; // Optional for in-house app
	property: string;
	buyer_name: string;
	buyer_email?: string;
	buyer_phone?: string;
	viewing_date: string; // ISO datetime string
	duration?: number;
	status?: "pending" | "scheduled" | "confirmed" | "completed" | "cancelled";
	notes?: string;
	agent?: string;
}

/**
 * Public viewing request interface (no authentication required)
 */
export interface PublicViewingRequestData {
	property: string;
	buyer_name: string;
	buyer_email?: string;
	buyer_phone?: string;
	preferred_date: string; // YYYY-MM-DD format
	preferred_time?: string; // HH:MM format
	duration?: number;
	notes?: string;
}

/**
 * Property Viewings API endpoints
 */
export const propertyViewingsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all property viewings (with filters, search, pagination)
		 */
		listViewings: builder.query<
			PropertyViewingListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				property?: string;
				buyer_email?: string;
				start_date?: string;
				end_date?: string;
				search?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.property) queryParams.append("property", params.property);
				if (params.buyer_email)
					queryParams.append("buyer_email", params.buyer_email);
				if (params.start_date)
					queryParams.append("start_date", params.start_date);
				if (params.end_date) queryParams.append("end_date", params.end_date);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				return {
					url: `/property-viewings/?${queryParams.toString()}`,
					method: "GET",
				};
			},
			providesTags: ["PropertyViewings"],
		}),

		/**
		 * Get property viewing details by ID
		 */
		getViewing: builder.query<PropertyViewing, string>({
			query: (id) => ({
				url: `/property-viewings/${id}/`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "PropertyViewings", id }],
		}),

		/**
		 * Create a new property viewing
		 */
		createViewing: builder.mutation<PropertyViewing, PropertyViewingData>({
			query: (data) => ({
				url: "/property-viewings/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["PropertyViewings"],
		}),

		/**
		 * Update a property viewing
		 */
		updateViewing: builder.mutation<
			PropertyViewing,
			{ id: string; data: Partial<PropertyViewingData> }
		>({
			query: ({ id, data }) => ({
				url: `/property-viewings/${id}/`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "PropertyViewings", id },
				"PropertyViewings",
			],
		}),

		/**
		 * Partially update a property viewing
		 */
		patchViewing: builder.mutation<
			PropertyViewing,
			{ id: string; data: Partial<PropertyViewingData> }
		>({
			query: ({ id, data }) => ({
				url: `/property-viewings/${id}/`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "PropertyViewings", id },
				"PropertyViewings",
			],
		}),

		/**
		 * Delete a property viewing
		 */
		deleteViewing: builder.mutation<void, string>({
			query: (id) => ({
				url: `/property-viewings/${id}/`,
				method: "DELETE",
			}),
			invalidatesTags: ["PropertyViewings"],
		}),

		/**
		 * Get viewings by date range
		 */
		getViewingsByDate: builder.query<
			PropertyViewing[],
			{ start_date: string; end_date: string }
		>({
			query: ({ start_date, end_date }) => ({
				url: `/property-viewings/by_date/?start_date=${start_date}&end_date=${end_date}`,
				method: "GET",
			}),
			providesTags: ["PropertyViewings"],
		}),

		/**
		 * Confirm a viewing
		 */
		confirmViewing: builder.mutation<PropertyViewing, string>({
			query: (id) => ({
				url: `/property-viewings/${id}/confirm/`,
				method: "POST",
			}),
			invalidatesTags: (result, error, id) => [
				{ type: "PropertyViewings", id },
				"PropertyViewings",
			],
		}),

		/**
		 * Cancel a viewing
		 */
		cancelViewing: builder.mutation<PropertyViewing, string>({
			query: (id) => ({
				url: `/property-viewings/${id}/cancel/`,
				method: "POST",
			}),
			invalidatesTags: (result, error, id) => [
				{ type: "PropertyViewings", id },
				"PropertyViewings",
			],
		}),

		/**
		 * Complete a viewing
		 */
		completeViewing: builder.mutation<PropertyViewing, string>({
			query: (id) => ({
				url: `/property-viewings/${id}/complete/`,
				method: "POST",
			}),
			invalidatesTags: (result, error, id) => [
				{ type: "PropertyViewings", id },
				"PropertyViewings",
			],
		}),

		/**
		 * Create a public viewing request (no authentication required)
		 */
		createPublicViewingRequest: builder.mutation<
			PropertyViewing,
			PublicViewingRequestData
		>({
			query: (data) => ({
				url: "/property-viewings/public/request/",
				method: "POST",
				body: data,
			}),
			// Don't invalidate tags since this is a public endpoint
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListViewingsQuery,
	useLazyListViewingsQuery,
	useGetViewingQuery,
	useLazyGetViewingQuery,
	useCreateViewingMutation,
	useUpdateViewingMutation,
	usePatchViewingMutation,
	useDeleteViewingMutation,
	useGetViewingsByDateQuery,
	useLazyGetViewingsByDateQuery,
	useConfirmViewingMutation,
	useCancelViewingMutation,
	useCompleteViewingMutation,
	useCreatePublicViewingRequestMutation,
} = propertyViewingsApi;
